import type { DeliveredListResponse } from '@shared/schemas';

/**
 * The default order of the Discovered LPs table (#712): by list number, highest
 * first ("Liste 23", then "Liste 22"...).
 *
 * Not by delivery date: lists sent before 2.2.0 have no real one (they carry
 * their licence's opening date), while the PO numbered every list of the first
 * clients. The page comes back to this order on every visit; a sort the client
 * picks is not kept.
 */

/**
 * The number after "Liste" / "List" in a list's name ("Liste 23 - Oct 2026" → 23,
 * "list #7" → 7), null when there is none. Other numbers, such as a year, are
 * not a list number.
 */
export function listNumber(name: string): number | null {
  const match = /\blist(?:es?|s)?\s*(?:n°|no\.?|#)?\s*(\d+)/i.exec(name);
  return match ? Number(match[1]) : null;
}

/** Most recent delivery first; a list with no date last. */
function compareDeliveredOn(a: DeliveredListResponse, b: DeliveredListResponse): number {
  if (a.sent_at === b.sent_at) return 0;
  if (a.sent_at === null) return 1;
  if (b.sent_at === null) return -1;
  return b.sent_at.localeCompare(a.sent_at);
}

/**
 * Numbered lists first, highest number first; lists with no number after them.
 * Equal numbers, and lists with none, go by delivery date, most recent first.
 */
export function compareDeliveredLists(a: DeliveredListResponse, b: DeliveredListResponse): number {
  const na = listNumber(a.name);
  const nb = listNumber(b.name);
  if (na !== nb) {
    if (na === null) return 1;
    if (nb === null) return -1;
    return nb - na;
  }
  return compareDeliveredOn(a, b);
}
